import React, {useState, useCallback, useEffect} from "react";
import { useSelector } from "react-redux";
import {
    TableContainer,
    TableContent,
    TableHeader,
    usePreventTableScrollingRef,
    Button,
    BUTTON_VARIANTS
  } from 'cdk-radial';
import { AgGridReact } from 'ag-grid-react';
import './DealerIntegrationTable.scss';
import {styledColumns} from './Columns';
import { Constants } from '../../constants/constants';
import { dealerIntegrationsTableClass, frameworkComponentsExtended } from './tableUtils';

const SEARCH_PLACEHOLDER = 'Search by dealer, cmf, app name or API name';
const FILE_NAME = 'dealer-integration-report.csv';
const TABLE_HEADER = 'Dealer Integration Report for Selected Dealers';
const COLUMNS_TO_EXPORT = [
  'dealer',
  'address',
  'cmf',
  'app',
  'subscriptionStatus',
  'api',
  'integrationStatus',
  'lastModified',
];
const AG_STATUS = {
    enabled: 'active',
    disabled: 'inactive'
};

const Table = ({dealerCount}) => {
    const [gridApi, setGridApi] = useState(null);
    const tableContentRef = usePreventTableScrollingRef();
    const [searchInputValue, setSearchInputValue] = useState('');
    const [totalRows, setTotalRows] = useState();
    const [data, setData] = useState();
    const { 
        subscriptionsByEntityId,
        marketplaceListedApps,
        notInMarketplaceApps,
        subscriptionIdCMFMap,
        connectionsByEntityId,
        accessGrants,
        apiIdToNameMapping,
    } = useSelector(state => state.dealerIntegrationsReportSlice);
    const { entityIdNameMapping, entityIdAddressMapping } = useSelector(state => state.loginDetailsSlice);

    const handleGridReady = useCallback(gridParams => {
        setGridApi(gridParams.api);
        gridParams.api.sizeColumnsToFit();
        setTotalRows(gridParams.api.getDisplayedRowCount());
    }, []);

    const handleSearchInputChange = searchInput => {
        setSearchInputValue(searchInput);
        if (gridApi) {
          gridApi.setQuickFilter(searchInput);
          // Refresh is needed for filtered cells highlighting to work properly
          gridApi.refreshCells({
            force: true
          });
        }
      };

    const handleDownloadClick = () => {
        gridApi.exportDataAsCsv({
            fileName: FILE_NAME,
            columnKeys: COLUMNS_TO_EXPORT
        });
    };

    useEffect(() => {
        let rowData = [];
        Object.keys(connectionsByEntityId)?.forEach(entityId => {
            connectionsByEntityId[entityId]?.forEach(connection => {
                const subscription = subscriptionsByEntityId[entityId]?.find(sub => sub.subscriptionId === connection.subscriptionId);
                const app = marketplaceListedApps[subscription?.solutionId] || notInMarketplaceApps[subscription?.solutionId] || 'N/A';
                const {api, departments} = connection;
                rowData.push({
                    dealer:  entityIdNameMapping[entityId] || 'N/A',
                    address: entityIdAddressMapping[entityId] || 'N/A',
                    cmf: subscriptionIdCMFMap[subscription?.subscriptionId] || 'N/A',
                    app,
                    subscriptionStatus: subscription?.status || 'N/A',
                    api: apiIdToNameMapping[api?.replace('api-v2-', '')] || 'N/A',
                    integrationStatus: connection?.status || 'N/A',
                    lastModified: connection?.lastUpdated || 'N/A',
                    configurations: connection.departments ? {
                        app,
                        api,
                        departments,
                        apiName: apiIdToNameMapping[api?.replace('api-v2-', '')] || api,
                    } : {}
                });
            });
        });

        accessGrants.forEach(ag => {
            const entityId = ag.dataOwnerOrgId;
            const subscription = subscriptionsByEntityId[entityId]?.find(sub => sub.subscriptionId === ag.subscriptionId);
            const app = marketplaceListedApps[subscription?.solutionId] || notInMarketplaceApps[subscription?.solutionId] || 'N/A';
            const departments = ag?.filters?.departments;
            rowData.push({
                dealer:  entityIdNameMapping[entityId] || 'N/A',
                address: entityIdAddressMapping[entityId] || 'N/A',
                cmf: subscriptionIdCMFMap[ag.subscriptionId] || 'N/A',
                app,
                subscriptionStatus: subscription?.status || 'N/A',
                api: ag?.asyncApiName || 'N/A',
                integrationStatus: AG_STATUS[ag?.status] || 'N/A',
                lastModified: subscription?.lastUpdated || 'N/A',
                configurations: departments?.length ? {
                    app,
                    api: '',
                    departments,
                    apiName: ag?.asyncApiName || 'N/A'
                } : {} 
            })
        });

        setData(rowData);
    }, [
        subscriptionsByEntityId,
        marketplaceListedApps,
        notInMarketplaceApps,
        subscriptionIdCMFMap,
        connectionsByEntityId
    ]);

    return (
        <>
            <TableContainer className={dealerIntegrationsTableClass}>
                <TableHeader
                    headerTitle={`${TABLE_HEADER} (${dealerCount})`} 
                    isDownloadable={false}
                    isPrintable={false}
                    isSearchable={true} 
                    onSearchInputChange={handleSearchInputChange}
                    searchInputValue={searchInputValue}
                    searchPlaceholder={SEARCH_PLACEHOLDER}
                />
                <TableContent ref={tableContentRef}>
                    <AgGridReact
                        defaultColDef={{
                            filter: true,
                            sortable: true
                        }}
                        frameworkComponents={frameworkComponentsExtended}
                        columnDefs={styledColumns}
                        onGridReady={handleGridReady}
                        rowData={data}
                        rowHeight={40}
                        cacheQuickFilter={true}
                        pagination={true}
                        paginationPageSize={10}
                    />
                </TableContent>
            </TableContainer>
            <div className="dealer-integration__footer">
                <Button
                    text={Constants.DOWNLOAD_REPORT}
                    variant={BUTTON_VARIANTS.SECONDARY}
                    onClick={handleDownloadClick}
                    isDisabled={!totalRows}
                />
            </div>
        </>
    )
};

export const DealerIntegrationTable = React.memo(Table);